import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Col, Row } from 'antd'

import A11yButton from '@/components/A11yButton'
import ImageWithFallBack from '@/components/ImageWithFallback'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { selectIsFuturetech } from '@/store/selectors/configSelector'
import { selectServicePackagePageByLanguage } from '@/store/selectors/userSelector'
import { setSelectedMachineInfo } from '@/store/slices/serviceCaseSlice'
import usePermission from '@/hooks/usePermission'
import PermissionWrapper from '@/HOCs/permissionWrapper'
import { SERVICE_ENTRY } from '@/libs/constants'
import { MODULE_SERVICE_RIGHTS, MODULE_SHOP_NAVIGATION_RIGHTS } from '@/libs/constants/modulerights'
import { linkGenerator } from '@/libs/utils/language'
import MaintenanceFuturetechImage from '@/assets/img/kft_maintenance.webp'
import RepairFuturetechImage from '@/assets/img/kft_repair.webp'
import ServicePartnerContactFuturetechImage from '@/assets/img/kft_service.webp'
import MaintenanceKaercherImage from '@/assets/img/maintenance.webp'
import RepairKaercherImage from '@/assets/img/repair.webp'
import ServicePartnerContactKaercherImage from '@/assets/img/servicepartner_kontakt.webp'

import styles from './ServicesMenu.module.scss'

const imagePaths = {
  karcher: {
    maintenance: MaintenanceKaercherImage,
    repair: RepairKaercherImage,
    servicePartnerContact: ServicePartnerContactKaercherImage,
  },
  futuretech: {
    maintenance: MaintenanceFuturetechImage,
    repair: RepairFuturetechImage,
    servicePartnerContact: ServicePartnerContactFuturetechImage,
  },
}

const ServiceMenu = ({ setShowSubmenu }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const {
    verifiedPermissions: [hasCreateRepairRight, hasCreateMaintenanceRight],
  } = usePermission([
    MODULE_SERVICE_RIGHTS.CREATE_REPAIR_SERVICE_CASE,
    MODULE_SERVICE_RIGHTS.CREATE_MAINTENANCE_SERVICE_CASE,
  ])

  const shouldHideCreateServiceCaseSession = !hasCreateRepairRight && !hasCreateMaintenanceRight

  const productMenuRef = useRef(null)

  const servicePackagePageByLanguage = useAppSelector(
    selectServicePackagePageByLanguage,
  )
  const isFuturetech = useAppSelector(selectIsFuturetech)
  const images = imagePaths[isFuturetech ? 'futuretech' : 'karcher']

  const handleClickOutside = useCallback(
    (event) => {
      if (
        productMenuRef.current
        && !productMenuRef.current.contains(event.target)
      ) {
        setShowSubmenu(false)
      }
    },
    [setShowSubmenu],
  )

  const handleServiceItemClick = useCallback(
    (serviceEntry) => {
      dispatch(
        setSelectedMachineInfo({
          serviceEntry,
          isChangedService: true,
        }),
      )

      setShowSubmenu(false)
      navigate(linkGenerator('/request-services'))
    },
    [dispatch, navigate, setShowSubmenu],
  )

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, {
      capture: true,
    })
    return () => {
      document.removeEventListener('click', handleClickOutside, {
        capture: true,
      })
    }
  }, [handleClickOutside])

  const renderServicePackageBlock = () => {
    const content = (
      <>
        <ImageWithFallBack src={images.servicePartnerContact} />
        <span className={styles['service-link__title']}>
          {t('service.headerMenu.servicePackages')}
        </span>
      </>
    )

    return servicePackagePageByLanguage
      ? (
        <A11yButton
          elementType="div"
          className={styles['service-link']}
          onKeyDown={(e) => e.preventDefault()}
          onClick={() => {
            setShowSubmenu(false)
          }}
        >
          <a href={servicePackagePageByLanguage} target="noreferrer">
            {content}
          </a>
        </A11yButton>
      )
      : (
        <A11yButton
          elementType="div"
          className={styles['service-link']}
          onClick={() => {
            setShowSubmenu(false)
            navigate(linkGenerator('/service/service-packages'))
          }}
        >
          {content}
        </A11yButton>
      )
  }

  return (
    <div ref={productMenuRef} className="header-submenu fadeInDown">
      <div className="header-submenu__container">
        <PermissionWrapper
          permission={MODULE_SHOP_NAVIGATION_RIGHTS.VIEW_SERVICE_PACKAGE_INFORMATION}
        >
          <Row gutter={10} justify="center">
            <Col
              className="header-submenu__item"
              lg={8}
              md={12}
              sm={24}
              xl={8}
              xs={24}
            >
              {renderServicePackageBlock()}
            </Col>
          </Row>
        </PermissionWrapper>
        {shouldHideCreateServiceCaseSession ? null : (
          <>
            <Row>
              <Col className="header-submenu__title" span={24}>
                <h3 className="header-submenu__title-heading">
                  {t('createServiceRequest')}
                </h3>
              </Col>
            </Row>
            <Row gutter={10}>
              <PermissionWrapper
                permission={MODULE_SERVICE_RIGHTS.CREATE_REPAIR_SERVICE_CASE}
              >
                <Col
                  className="header-submenu__item"
                  lg={6}
                  md={6}
                  sm={8}
                  xl={6}
                  xs={12}
                >
                  <A11yButton
                    elementType="div"
                    className={styles['service-link']}
                    onClick={() => {
                      handleServiceItemClick(SERVICE_ENTRY.REPAIR)
                    }}
                  >
                    <ImageWithFallBack src={images.repair} />
                    <span className={styles['service-link__title']}>
                      {t('service.headerMenu.requestRepair')}
                    </span>
                  </A11yButton>
                </Col>
              </PermissionWrapper>
              <PermissionWrapper
                permission={MODULE_SERVICE_RIGHTS.CREATE_MAINTENANCE_SERVICE_CASE}
              >
                <Col
                  className="header-submenu__item"
                  lg={6}
                  md={6}
                  sm={8}
                  xl={6}
                  xs={12}
                >
                  <A11yButton
                    elementType="div"
                    className={styles['service-link']}
                    onClick={() => {
                      handleServiceItemClick(SERVICE_ENTRY.MAINTENANCE)
                    }}
                  >
                    <ImageWithFallBack src={images.maintenance} />
                    <span className={styles['service-link__title']}>
                      {t('service.headerMenu.requestMaintenance')}
                    </span>
                  </A11yButton>
                </Col>
              </PermissionWrapper>
            </Row>
          </>
        )}
      </div>
    </div>
  )
}

export default ServiceMenu
